import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import * as z from "zod";
import { api } from "@/lib/http";
import { paginatedListSchema } from "@/lib/utils/zod";
import { groupSchema } from "@/lib/queries/groups";
import { entitySchema } from "@/lib/queries/entities";

export const secureLLMChatSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  llm_model_id: z.uuid(),
});

type LLMProvider =
  | "OPENAI"
  | "ANTHROPIC"
  | "GEMINI"
  | "DEEPSEEK"
  | (string & {});

const llmModelSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  provider: z.string().transform((v) => v as LLMProvider),
});

export const secureLLMChatDetailSchema = secureLLMChatSchema.extend({
  owner_id: z.uuid(),
  llm_model: llmModelSchema,
  entity_counting: z.record(z.string(), z.number()),
});

export const secureLLMChatsListSchema =
  paginatedListSchema(secureLLMChatSchema);

const secureLlmMessageSchema = z.object({
  id: z.uuid(),
  masked_text: z.string(),
  original_text: z.string(),
  texted_by: z.string(),
  created_at: z.iso.datetime(),
  origin_timestamp: z.iso.datetime(),
  file_path: z.string(),
});

const secureLlmHistorySchema = paginatedListSchema(secureLlmMessageSchema);

export const secureLlmBaseEntitySchema = z.object({
  entity_text: z.string(),
  masked_entity_text: z.string(),
  confidence_score: z.number(),
  start_index: z.number(),
  end_index: z.number(),
});

export const secureLlmMessageEntitySchema = secureLlmBaseEntitySchema.extend({
  entity_name: z.string(),
  existing: z.boolean(),
});
export const secureLlmEntitySchema = secureLlmBaseEntitySchema.extend({
  id: z.uuid(),
  entity_type: entitySchema,

  is_critical_pii: z.boolean(),
  criticality_level: z.string(),
  action_type: z.string(),
  detected_at: z.iso.datetime(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  content_type: z.string(),
});

export const secureLlmEntitiesListSchema = paginatedListSchema(
  secureLlmEntitySchema,
);

export type SecureLLMChatDetail = z.infer<typeof secureLLMChatDetailSchema>;
export type SecureLLMChatsListEntry = z.infer<typeof secureLLMChatSchema>;
export type SecureLLMChatsList = z.infer<typeof secureLLMChatsListSchema>;
export type SecureLLMMessage = z.infer<typeof secureLlmMessageSchema>;
export type SecureLLMHistory = z.infer<typeof secureLlmHistorySchema>;
export type SecureLLMBaseEntity = z.infer<typeof secureLlmBaseEntitySchema>;
export type SecureLLMMessageEntity = z.infer<
  typeof secureLlmMessageEntitySchema
>;
export type SecureLLMEntity = z.infer<typeof secureLlmEntitySchema>;
export type SecureLLMEntityList = z.infer<typeof secureLlmEntitiesListSchema>;

const llmApiKeySchemaBase = z.object({
  name: z.string(),
  description: z.string(),
});

export const llmApiKeySchema = llmApiKeySchemaBase.extend({
  id: z.uuid(),
  llm_models: z.array(llmModelSchema),
  groups: z.array(groupSchema),
});
export const paginatedLlmApiKeysSchema = paginatedListSchema(
  llmApiKeySchemaBase.extend({
    id: z.uuid(),
    api_key: z.string(),
    llm_models: z.array(llmModelSchema),
    groups: z.array(groupSchema),
  }),
);

export const llmApiKeyFormSchema = llmApiKeySchemaBase.extend({
  api_key: z.string().min(1, "API key is required"),
  llm_model_ids: z.array(z.uuid()),
  group_ids: z.array(z.uuid()),
});
export const llmApiKeyEditFormSchema = llmApiKeyFormSchema.extend({
  api_key: z.string().optional(),
});

export type LlmModel = z.infer<typeof llmModelSchema>;
export type ApiKey = z.infer<typeof llmApiKeySchema>;
export type PaginatedApiKeys = z.infer<typeof paginatedLlmApiKeysSchema>;
export type ApiKeyFormData = z.infer<typeof llmApiKeyFormSchema>;
export type ApiKeyEditFormData = z.infer<typeof llmApiKeyEditFormSchema>;

type PaginationParams = {
  page: number;
  per_page?: number;
};

export function chatsQueryOptions(params: PaginationParams) {
  return queryOptions({
    queryKey: ["secureLLM", "chats", "list", params],
    queryFn() {
      return api.raw
        .get<SecureLLMChatsList>("api/v1/chats/paginated", {
          searchParams: {
            page: params.page.toString(),
            per_page: (params.per_page ?? 10).toString(),
          },
        })
        .json();
    },
  });
}

export function chatsInfiniteQueryOptions(options?: { per_page?: number }) {
  return infiniteQueryOptions({
    queryKey: ["secureLLM", "chats", "list-infinite", options],
    queryFn: ({ pageParam = 1 }) =>
      api.raw
        .get<SecureLLMChatsList>("api/v1/chats/paginated", {
          searchParams: {
            page: pageParam.toString(),
            per_page: (options?.per_page ?? 15).toString(),
          },
        })
        .json(),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage.next_page) return null;
      return lastPageParam + 1;
    },
    initialPageParam: 1,
  });
}

export function chatDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["secureLLM", "chats", id, "detail"],
    queryFn: () =>
      api.raw.get<SecureLLMChatDetail>(`api/v1/chats/${id}`).json(),
  });
}

export function chatHistoryInfiniteQueryOptions(
  id: string,
  options?: { limit?: number },
) {
  return infiniteQueryOptions({
    queryKey: ["secureLLM", "chats", id, "history", options],
    queryFn: ({ pageParam: url }) => api.raw.get<SecureLLMHistory>(url).json(),

    initialPageParam: `api/v1/chats/${id}/history${options?.limit ? `?limit=${options.limit}` : ""}`,

    getNextPageParam: (l, _allPages, path) => {
      if (!l.next_page) return null;

      const currentUrl = new URL("/" + path, "http://nowhere");
      const after = currentUrl.searchParams.get("after");
      const limit = parseInt(currentUrl.searchParams.get("limit") ?? "10");
      if (after && l.items.length < limit) return null;

      const nextUrl = new URL(l.next_page);
      const nextPage =
        nextUrl.pathname.slice(1) + nextUrl.search + nextUrl.hash;
      return nextPage;
    },
    getPreviousPageParam: (l, _allPages, path) => {
      if (!l.previous_page) return null;

      const currentUrl = new URL("/" + path, "http://nowhere");
      const before = currentUrl.searchParams.get("before");
      const limit = parseInt(currentUrl.searchParams.get("limit") ?? "10");
      if (before && l.items.length < limit) return null;

      const prevUrl = new URL(l.previous_page);
      const prevPage =
        prevUrl.pathname.slice(1) + prevUrl.search + prevUrl.hash;
      return prevPage;
    },
  });
}

export function userActiveModelsQueryOptions() {
  return queryOptions({
    queryKey: ["secureLLM", "user-active-models"],
    queryFn() {
      return api.raw
        .get<Array<LlmModel>>("api/v1/llm/user-active-models")
        .json();
    },
  });
}

export function chatFoundEntitiesQueryOptions(
  id: string,
  params: PaginationParams,
) {
  return queryOptions({
    queryKey: ["secureLLM", "chats", id, "foundEntities", params],
    queryFn() {
      return api.raw
        .get<SecureLLMEntityList>(`api/v1/chats/${id}/found-entities`, {
          searchParams: {
            page: params.page.toString(),
            per_page: (params.per_page ?? 10).toString(),
          },
        })
        .json();
    },
  });
}

// administration

export function llmProvidersQueryOptions(params?: { search?: string }) {
  return queryOptions({
    queryKey: ["secureLLM", "admin", "llm-providers", params],
    queryFn() {
      const searchParams = new URLSearchParams();
      if (params?.search) searchParams.set("search", params.search);
      return api.raw
        .get<Array<LLMProvider>>("api/v1/llm/providers", { searchParams })
        .json();
    },
  });
}

export function llmModelsQueryOptions(params: {
  provider?: LLMProvider;
  search?: string;
}) {
  return queryOptions({
    queryKey: ["secureLLM", "admin", "llm-models", params],
    queryFn({ client }) {
      const searchParams = new URLSearchParams();
      if (params.search) searchParams.set("search", params.search);
      if (params.provider) searchParams.set("provider_type", params.provider);

      const res = api.raw
        .get<Array<LlmModel>>("api/v1/llm/models", {
          searchParams,
        })
        .json();

      res.then((models) =>
        models.forEach((m) =>
          client.setQueryData(llmModelDetailQueryOptions(m.id).queryKey, m),
        ),
      );

      return res;
    },
  });
}

export function llmModelDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["secureLLM", "admin", "llm-models", "details", id],
    queryFn() {
      return api.raw.get<LlmModel>(`api/v1/llm/models/${id}`).json();
    },
  });
}

export function llmApiKeysQueryOptions(params: {
  search?: string;
  page: number;
  per_page?: number;
}) {
  return queryOptions({
    queryKey: ["secureLLM", "admin", "api-keys", params],
    queryFn() {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        per_page: (params.per_page ?? 10).toString(),
      });
      if (params.search) searchParams.set("search", params.search);

      return api.raw
        .get<PaginatedApiKeys>("api/v1/llm/api-keys/paginated", {
          searchParams,
        })
        .json();
    },
  });
}

export function llmApiKeyDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["secureLLM", "admin", "api-keys", "details", id],
    queryFn() {
      return api.raw.get<ApiKey>(`api/v1/llm/api-keys/${id}`).json();
    },
  });
}
